// Pages the account emails link to, plus the one-time birthday step: reset a forgotten password (link or code),
// "this wasn't me" account lock-down, confirm an email address, and confirm age for Google/Apple sign-ups.
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const inp = "w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary";
const Shell = ({ title, children, testid }) => (
  <main className="mx-auto max-w-md px-4 py-12" data-testid={testid}>
    <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
    <div className="mt-5 space-y-3">{children}</div>
  </main>
);
const strong = (p) => p.length >= 8;

export function ForgotPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const nav = useNavigate();
  const [valid, setValid] = useState(token ? null : true);
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { document.title = "Reset your password · Fable+"; if (token) api("/auth/reset-password/check?token=" + encodeURIComponent(token), { auth: false }).then((r) => setValid(!!r.valid)).catch(() => setValid(false)); }, [token]);
  const reset = async () => {
    setBusy(true);
    try { await api("/auth/reset-password", { method: "POST", auth: false, body: token ? { token, new_password: pw } : { email: email.trim(), code, new_password: pw } }); toast.success("Password changed — sign in with your new password"); nav("/login"); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  if (token) return (
    <Shell title="Choose a new password" testid="reset-password">
      {valid === null ? <p className="text-sm text-muted-foreground">Checking your link…</p> : !valid ? <><p className="text-sm">This link has expired or was already used.</p><Link className="text-sm underline" to="/forgot-password">Send a new one</Link></> : <>
        <input className={inp} type="password" autoComplete="new-password" placeholder="New password (8+ characters)" aria-label="New password" value={pw} onChange={(e) => setPw(e.target.value)} />
        <Button className="w-full" disabled={!strong(pw) || busy} onClick={reset}>Set new password</Button>
        <p className="text-xs text-muted-foreground">Every device signed in to your account will be signed out.</p></>}
    </Shell>
  );
  return (
    <Shell title="Forgot your password?" testid="forgot-password">
      <p className="text-sm text-muted-foreground">Enter your email and we'll send a link and a 6-digit code.</p>
      <input className={inp} type="email" autoComplete="email" placeholder="you@example.com" aria-label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
      <Button className="w-full" disabled={email.trim().length < 3 || busy} onClick={async () => { setBusy(true); try { await api("/auth/forgot-password", { method: "POST", auth: false, body: { email: email.trim() } }); setSent(true); } catch (e) { toast.error(e.message); } finally { setBusy(false); } }}>{sent ? "Send again" : "Send reset email"}</Button>
      {sent ? <>
        <p className="text-sm">If an account uses that email, the message is on its way. Tap the link, or enter the code here:</p>
        <input className={inp} inputMode="numeric" maxLength={6} placeholder="6-digit code" aria-label="Code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
        <input className={inp} type="password" autoComplete="new-password" placeholder="New password (8+ characters)" aria-label="New password" value={pw} onChange={(e) => setPw(e.target.value)} />
        <Button className="w-full" disabled={code.length !== 6 || !strong(pw) || busy} onClick={reset}>Set new password</Button>
      </> : null}
      <Link className="block text-sm underline" to="/login">Back to sign in</Link>
    </Shell>
  );
}

export function SecureAccount() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [valid, setValid] = useState(null);
  const [done, setDone] = useState(null);
  useEffect(() => { document.title = "Secure your account · Fable+"; api("/auth/secure-account/check?token=" + encodeURIComponent(token), { auth: false }).then((r) => setValid(!!r.valid)).catch(() => setValid(false)); }, [token]);
  return (
    <Shell title="Wasn't you?" testid="secure-account">
      {done ? <><p className="text-sm">Done. Every device has been signed out, including the one that signed in. Choose a new password now.</p>
        <Link to={"/forgot-password?token=" + encodeURIComponent(done.reset_token)}><Button className="w-full">Choose a new password</Button></Link></>
        : valid === null ? <p className="text-sm text-muted-foreground">Checking your link…</p>
        : !valid ? <><p className="text-sm">This link has expired or was already used. If you still don't recognise a sign-in, reset your password.</p><Link className="text-sm underline" to="/forgot-password">Reset password</Link></>
        : <><p className="text-sm">We'll sign out every device on your account straight away, then you choose a new password.</p>
          <Button className="w-full" variant="destructive" onClick={async () => { try { setDone(await api("/auth/secure-account", { method: "POST", auth: false, body: { token } })); } catch (e) { toast.error(e.message); } }}>Sign out everywhere</Button></>}
    </Shell>
  );
}

export function VerifyEmail() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [state, setState] = useState("working");
  useEffect(() => { document.title = "Confirm your email · Fable+"; if (!token) { setState("bad"); return; } api("/auth/verify-email/link", { method: "POST", auth: false, body: { token } }).then(() => setState("ok")).catch(() => setState("bad")); }, [token]);
  return (
    <Shell title="Confirm your email" testid="verify-email">
      {state === "working" ? <p className="text-sm text-muted-foreground">Confirming…</p> : state === "ok" ? <><p className="text-sm">Your email is confirmed. Thanks!</p><Link className="text-sm underline" to="/">Go to Fable+</Link></>
        : <><p className="text-sm">This link has expired or was already used. Sign in and we'll send a fresh one from Settings.</p><Link className="text-sm underline" to="/settings">Settings</Link></>}
    </Shell>
  );
}

export function ConfirmAge() {
  const nav = useNavigate();
  const [dob, setDob] = useState("");
  return (
    <Shell title="Your date of birth" testid="confirm-age">
      <p className="text-sm text-muted-foreground">We ask everyone once so we can keep under-18s safe. It's never shown on your profile.</p>
      <input className={inp} type="date" max={new Date().toISOString().slice(0, 10)} aria-label="Date of birth" value={dob} onChange={(e) => setDob(e.target.value)} />
      <Button className="w-full" disabled={!dob} onClick={async () => { try { await api("/auth/confirm-age", { method: "POST", body: { date_of_birth: dob } }); toast.success("Thanks"); nav("/"); } catch (e) { toast.error(e.message); } }}>Confirm</Button>
    </Shell>
  );
}
